import {
  PAID_PASS_BANK_LINE,
  QUESTION_BANK_FULL,
  QUESTION_BANK_LABEL,
  QUESTION_BANK_SIZE,
  SESSION_QUESTION_TARGET,
} from './product';
import { MOCK_FORM_COUNT } from './mockForms';

/** Moved here from mockForms.js (CFA-63) so mockForms.js stays import-free for data/topics.js. */
export const MOCK_FORMS_PRODUCT_LINE = `${MOCK_FORM_COUNT} named timed sessions (${SESSION_QUESTION_TARGET} questions each)`;

/** Floor founding price — weekly tiers start here. */
export const PASS_PRICE_USD = 29;
/** Courtesy refund window before any paid session or drill — keep in sync with Terms. */
export const REFUND_GRACE_DAYS = 7;
export const ANCHOR_PRICE_USD = 99;
export const PASS_ACCESS_DAYS = 365;
export const PASS_LABEL = 'Lifetime access';
export const FREE_SESSION_KEY = 'cfa_free_session_v1';
export const FREE_SESSION_KEY_L2 = 'cfa_free_session_l2_v1';
export const FREE_SESSION_KEY_L3 = 'cfa_free_session_l3_v1';
/** Legacy per-question trial cap — migration only; product is one free Mock 1. */
export const FREE_TRIAL_QUESTIONS = 10;
export const FREE_TRIAL_KEY = 'cfa_free_trial_used_v2';

/** Last calendar day (UTC) to buy at founding tiers. After this, list $99 applies. */
export const FOUNDING_OFFER_END = '2026-09-30';
export const FOUNDING_OFFER_END_LABEL = '30 September 2026';

/**
 * Weekly founding tiers (UTC, inclusive through `until` date).
 * Keep in sync with netlify/functions/_pass.js
 */
export const FOUNDING_PRICE_TIERS = [
  { until: '2026-08-30', priceUsd: 29 },
  { until: '2026-09-06', priceUsd: 39 },
  { until: '2026-09-13', priceUsd: 49 },
  { until: '2026-09-20', priceUsd: 59 },
  { until: '2026-09-30', priceUsd: 69 },
];

/**
 * One-day Labor Day flash — America/New_York calendar day only.
 * Holds last week's $39; ladder resumes at $49 after midnight NY.
 * Keep in sync with netlify/functions/_pass.js
 */
export const LABOR_DAY_FLASH = {
  id: 'labor-day-2026',
  dateNy: '2026-09-07',
  priceUsd: 39,
  resumePriceUsd: 49,
  label: 'Labor Day flash',
  endsLabel: 'tonight (New York time)',
};

export const FOUNDING_BADGE = 'Founding price';
export const ANCHOR_COPY = `$${ANCHOR_PRICE_USD} lifetime`;
/** Clarifies one-time charge — not a subscription. */
export const ONE_TIME_PAYMENT_LINE =
  'Pay once for lifetime access — does not auto-renew';

/** Level 2 / 3 list price after founding window. */
/** Level 2 / Level 3 list price (shared ladder). */
export const L2_ANCHOR_PRICE_USD = 199;
/** @deprecated Alias — L3 uses the same list as L2. */
export const L3_ANCHOR_PRICE_USD = L2_ANCHOR_PRICE_USD;
export const L1_MONTHLY_USD = 19;
export const L2_MONTHLY_USD = 29;
export const L3_MONTHLY_USD = 29;
/**
 * L2/L3 founding ladder (UTC). Same end date as L1.
 * Keep in sync with netlify/functions/_skus.js
 */
export const L2_FOUNDING_PRICE_TIERS = [
  { until: '2026-09-13', priceUsd: 149 },
  { until: '2026-09-20', priceUsd: 169 },
  { until: '2026-09-30', priceUsd: 184 },
];

export const VALUE_BULLETS = [
  `Eight full ${SESSION_QUESTION_TARGET}-question timed mocks in CBT format (${QUESTION_BANK_LABEL} original practice items)`,
  `${MOCK_FORMS_PRODUCT_LINE} — Mock 1 always free (score only); lifetime unlocks Mocks 2–8, explanations, drills, and future L1 mocks we add`,
  'Post-mock analysis: weak topics ranked, timing tells, and next drills',
  'Official 2026 topic weights on every mock — no repeated stems within or across the eight forms',
  QUESTION_BANK_FULL,
  'On-screen TI BA II Plus calculator during every mock and drill',
  'All ten topic drills with explanations for every item',
  ONE_TIME_PAYMENT_LINE,
];

function endOfFoundingOffer(now = new Date()) {
  return new Date(`${FOUNDING_OFFER_END}T23:59:59.999Z`);
}

function dayAfter(isoDate) {
  const d = new Date(`${isoDate}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d;
}

function formatShortDate(isoDate) {
  const d = new Date(`${isoDate}T12:00:00.000Z`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

function splitTimeRemaining(ms) {
  if (ms <= 0) {
    return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0 };
  }
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return { expired: false, days, hours, minutes, seconds, totalMs: ms };
}

/** YYYY-MM-DD in America/New_York. */
export function americaNyDateString(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** First UTC ms of the next America/New_York calendar day after `dateNy` (YYYY-MM-DD). */
export function endOfAmericaNyDayMs(dateNy) {
  const [y, m, d] = String(dateNy)
    .split('-')
    .map((n) => Number(n));
  if (!y || !m || !d) return 0;
  // Binary search the first instant whose NY date is after dateNy.
  let lo = Date.parse(`${dateNy}T00:00:00.000Z`) - 36 * 3600000;
  let hi = Date.parse(`${dateNy}T00:00:00.000Z`) + 48 * 3600000;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const ny = americaNyDateString(new Date(mid));
    if (ny <= dateNy) lo = mid + 1;
    else hi = mid;
  }
  return lo - 1;
}

export function isLaborDayFlashActive(now = new Date()) {
  return americaNyDateString(now) === LABOR_DAY_FLASH.dateNy;
}

export function getDynamicPricing(now = new Date()) {
  const foundingEnd = endOfFoundingOffer(now);
  if (now.getTime() > foundingEnd.getTime()) {
    return {
      active: false,
      expired: true,
      priceUsd: ANCHOR_PRICE_USD,
      anchorUsd: ANCHOR_PRICE_USD,
      savingsUsd: 0,
      tierIndex: -1,
      nextPriceUsd: null,
      nextPriceAt: null,
      nextPriceLabel: null,
      msUntilPriceIncrease: 0,
      flash: null,
    };
  }

  let tierIndex = FOUNDING_PRICE_TIERS.length - 1;
  for (let i = 0; i < FOUNDING_PRICE_TIERS.length; i += 1) {
    const until = new Date(`${FOUNDING_PRICE_TIERS[i].until}T23:59:59.999Z`);
    if (now.getTime() <= until.getTime()) {
      tierIndex = i;
      break;
    }
  }

  const tier = FOUNDING_PRICE_TIERS[tierIndex];
  const isLastTier = tierIndex === FOUNDING_PRICE_TIERS.length - 1;
  let nextPriceUsd = isLastTier ? ANCHOR_PRICE_USD : FOUNDING_PRICE_TIERS[tierIndex + 1].priceUsd;
  let nextPriceAt = isLastTier ? dayAfter(FOUNDING_OFFER_END) : dayAfter(tier.until);
  let msUntilPriceIncrease = Math.max(0, nextPriceAt.getTime() - now.getTime());
  let priceUsd = tier.priceUsd;
  let flash = null;

  if (isLaborDayFlashActive(now)) {
    const flashEndsMs = endOfAmericaNyDayMs(LABOR_DAY_FLASH.dateNy);
    priceUsd = LABOR_DAY_FLASH.priceUsd;
    nextPriceUsd = LABOR_DAY_FLASH.resumePriceUsd;
    nextPriceAt = new Date(flashEndsMs + 1);
    msUntilPriceIncrease = Math.max(0, flashEndsMs - now.getTime());
    flash = {
      id: LABOR_DAY_FLASH.id,
      label: LABOR_DAY_FLASH.label,
      endsLabel: LABOR_DAY_FLASH.endsLabel,
      endsAt: flashEndsMs,
    };
  }

  return {
    active: true,
    expired: false,
    priceUsd,
    anchorUsd: ANCHOR_PRICE_USD,
    savingsUsd: ANCHOR_PRICE_USD - priceUsd,
    tierIndex,
    tierUntil: tier.until,
    nextPriceUsd,
    nextPriceAt,
    nextPriceLabel: formatShortDate(
      isLastTier ? FOUNDING_OFFER_END : FOUNDING_PRICE_TIERS[tierIndex + 1]?.until || FOUNDING_OFFER_END
    ),
    msUntilPriceIncrease,
    flash,
  };
}

/** Level 2 / Level 3 dynamic founding ladder → list $199. */
export function getDynamicPricingL2(now = new Date()) {
  const foundingEnd = endOfFoundingOffer(now);
  if (now.getTime() > foundingEnd.getTime()) {
    return {
      active: false,
      expired: true,
      priceUsd: L2_ANCHOR_PRICE_USD,
      anchorUsd: L2_ANCHOR_PRICE_USD,
      savingsUsd: 0,
      tierIndex: -1,
      nextPriceUsd: null,
      nextPriceAt: null,
      nextPriceLabel: null,
      msUntilPriceIncrease: 0,
      flash: null,
    };
  }

  let tierIndex = L2_FOUNDING_PRICE_TIERS.length - 1;
  for (let i = 0; i < L2_FOUNDING_PRICE_TIERS.length; i += 1) {
    const until = new Date(`${L2_FOUNDING_PRICE_TIERS[i].until}T23:59:59.999Z`);
    if (now.getTime() <= until.getTime()) {
      tierIndex = i;
      break;
    }
  }

  const tier = L2_FOUNDING_PRICE_TIERS[tierIndex];
  const isLastTier = tierIndex === L2_FOUNDING_PRICE_TIERS.length - 1;
  const nextPriceUsd = isLastTier
    ? L2_ANCHOR_PRICE_USD
    : L2_FOUNDING_PRICE_TIERS[tierIndex + 1].priceUsd;
  const nextPriceAt = isLastTier ? dayAfter(FOUNDING_OFFER_END) : dayAfter(tier.until);
  const msUntilPriceIncrease = Math.max(0, nextPriceAt.getTime() - now.getTime());

  return {
    active: true,
    expired: false,
    priceUsd: tier.priceUsd,
    anchorUsd: L2_ANCHOR_PRICE_USD,
    savingsUsd: L2_ANCHOR_PRICE_USD - tier.priceUsd,
    tierIndex,
    tierUntil: tier.until,
    nextPriceUsd,
    nextPriceAt,
    nextPriceLabel: formatShortDate(
      isLastTier
        ? FOUNDING_OFFER_END
        : L2_FOUNDING_PRICE_TIERS[tierIndex + 1]?.until || FOUNDING_OFFER_END
    ),
    msUntilPriceIncrease,
    flash: null,
  };
}

export function currentL2PassPriceUsd(now = new Date()) {
  return getDynamicPricingL2(now).priceUsd;
}

/** Alias — L3 shares the L2 founding ladder and list price. */
export function getDynamicPricingL3(now = new Date()) {
  return getDynamicPricingL2(now);
}

export function currentL3PassPriceUsd(now = new Date()) {
  return getDynamicPricingL3(now).priceUsd;
}

/** Live countdown until founding offer ends (UTC end of FOUNDING_OFFER_END). */
export function getFoundingOfferTimeRemaining(now = new Date()) {
  const ms = endOfFoundingOffer(now).getTime() - now.getTime();
  return splitTimeRemaining(ms);
}

/** Live countdown until the current founding tier price increases. */
export function getPriceIncreaseTimeRemaining(now = new Date()) {
  const pricing = getDynamicPricing(now);
  if (!pricing.active || !pricing.msUntilPriceIncrease) {
    return splitTimeRemaining(0);
  }
  return splitTimeRemaining(pricing.msUntilPriceIncrease);
}

/** True when the next weekly price step is within 72 hours (UTC). */
export function isPriceIncreaseUrgent(now = new Date()) {
  const pricing = getDynamicPricing(now);
  return (
    pricing.active &&
    pricing.msUntilPriceIncrease > 0 &&
    pricing.msUntilPriceIncrease <= 72 * 3600000
  );
}

export function priceIncreaseDeltaUsd(now = new Date()) {
  const pricing = getDynamicPricing(now);
  if (!pricing.active || !pricing.nextPriceUsd) return 0;
  return pricing.nextPriceUsd - pricing.priceUsd;
}

/** Canonical price line — homepage, pricing, Stripe, score email. */
export function foundingPriceSentence(now = new Date()) {
  const price = currentPassPriceUsd(now);
  const pricing = getDynamicPricing(now);
  if (!pricing.active) {
    return `$${ANCHOR_PRICE_USD} lifetime. ${ONE_TIME_PAYMENT_LINE}.`;
  }
  if (pricing.flash) {
    return `$${price} lifetime Labor Day flash — ends ${LABOR_DAY_FLASH.endsLabel}. Then $${pricing.nextPriceUsd} founding through ${FOUNDING_OFFER_END_LABEL}, then $${ANCHOR_PRICE_USD}. ${ONE_TIME_PAYMENT_LINE}.`;
  }
  if (pricing.nextPriceUsd < ANCHOR_PRICE_USD) {
    const tierEnd = formatShortDate(pricing.tierUntil);
    return `$${price} lifetime founding through ${tierEnd}, then $${pricing.nextPriceUsd} founding (list $${ANCHOR_PRICE_USD} after ${FOUNDING_OFFER_END_LABEL}). ${ONE_TIME_PAYMENT_LINE}.`;
  }
  return `$${price} lifetime founding until ${FOUNDING_OFFER_END_LABEL}, then $${ANCHOR_PRICE_USD}. ${ONE_TIME_PAYMENT_LINE}.`;
}

/** Short tier-step line for CTAs / results (empty when not on a mid-ladder founding tier). */
export function foundingTierStepLine(now = new Date()) {
  const pricing = getDynamicPricing(now);
  if (!pricing.active || pricing.flash) return '';
  if (!(pricing.nextPriceUsd < ANCHOR_PRICE_USD)) return '';
  return `$${pricing.priceUsd} through ${formatShortDate(pricing.tierUntil)} · then $${pricing.nextPriceUsd}`;
}

/** Public one-liner for pricing surfaces — keep simple; tiers stay in getDynamicPricing for Stripe. */
export function lockInPriceLine(now = new Date()) {
  return foundingPriceSentence(now);
}

export function foundingHeadline(now = new Date()) {
  const price = currentPassPriceUsd(now);
  return `Lifetime access — $${price}`;
}

/**
 * Regional sale hook for CTA copy. utils/pricingTier.js registers a getter that returns the
 * server-resolved active sale ({ priceUsd, tier, country, endsAt }) or null. Unregistered
 * (build-time SEO, crawler shell, emails) it is always null, so public copy stays list.
 * Deliberately NOT used by currentPassPriceUsd(), which feeds JSON-LD.
 */
let saleLifetimeSource = () => null;

export function setSaleLifetimeSource(fn) {
  saleLifetimeSource = typeof fn === 'function' ? fn : () => null;
}

export function activeSaleLifetime() {
  try {
    const sale = saleLifetimeSource();
    return sale && Number.isFinite(sale.priceUsd) ? sale : null;
  } catch {
    return null;
  }
}

/** Lifetime price for CTAs: the active regional sale price, else the current list/founding price. */
export function lifetimeCtaPriceUsd(now = new Date()) {
  const sale = activeSaleLifetime();
  const regular = currentPassPriceUsd(now);
  return sale ? Math.min(sale.priceUsd, regular) : regular;
}

export function checkoutCta(now = new Date()) {
  return `Purchase lifetime access — $${lifetimeCtaPriceUsd(now)}`;
}

/** Post-Mock-1 results + score email — keep in sync with netlify/functions/_pass.js */
export function unlockMocksFoundingCta(now = new Date()) {
  if (activeSaleLifetime()) {
    return `Unlock L1 Mocks 2–8 + every explanation — $${lifetimeCtaPriceUsd(now)} lifetime`;
  }
  const price = currentPassPriceUsd(now);
  const pricing = getDynamicPricing(now);
  if (!pricing.active) {
    return `Unlock L1 Mocks 2–8 + every explanation — $${ANCHOR_PRICE_USD} lifetime`;
  }
  return `Unlock L1 Mocks 2–8 + every explanation — $${price} lifetime`;
}

/** Home / leaderboard hero CTAs — monthly-first wording using list monthly + quoted lifetime. */
export function unlockMocksMonthlyLeadCta(now = new Date()) {
  return `Unlock L1 Mocks 2–8 + every explanation — from $${L1_MONTHLY_USD}/mo or $${lifetimeCtaPriceUsd(now)} lifetime`;
}

export function getFoundingOfferState(now = new Date()) {
  const pricing = getDynamicPricing(now);
  const offerRemaining = getFoundingOfferTimeRemaining(now);

  if (pricing.expired) {
    return {
      active: false,
      expired: true,
      daysLeft: 0,
      hoursLeft: 0,
      minutesLeft: 0,
      endLabel: FOUNDING_OFFER_END_LABEL,
      priceUsd: ANCHOR_PRICE_USD,
      nextPriceUsd: null,
      nextPriceLabel: null,
      savingsUsd: 0,
    };
  }

  return {
    active: true,
    expired: false,
    daysLeft: offerRemaining.days,
    hoursLeft: offerRemaining.hours,
    minutesLeft: offerRemaining.minutes,
    endLabel: FOUNDING_OFFER_END_LABEL,
    priceUsd: pricing.priceUsd,
    nextPriceUsd: pricing.nextPriceUsd,
    nextPriceLabel: pricing.nextPriceLabel,
    savingsUsd: pricing.savingsUsd,
    msUntilPriceIncrease: pricing.msUntilPriceIncrease,
    flash: pricing.flash || null,
  };
}

export function currentPassPriceUsd(now = new Date()) {
  return getDynamicPricing(now).priceUsd;
}

export function foundingBadgeLabel(now = new Date()) {
  const pricing = getDynamicPricing(now);
  return pricing.flash?.label || FOUNDING_BADGE;
}

export function foundingOfferPublicLine(now = new Date()) {
  const sale = activeSaleLifetime();
  if (sale) {
    return `$${lifetimeCtaPriceUsd(now)} lifetime during the regional sale (list $${ANCHOR_PRICE_USD}). ${ONE_TIME_PAYMENT_LINE}.`;
  }
  return foundingPriceSentence(now);
}

export function foundingOfferUntilLine(now = new Date()) {
  const price = currentPassPriceUsd(now);
  return `$${price} lifetime founding rate through ${FOUNDING_OFFER_END_LABEL}`;
}

export function freeMockPublicLine() {
  return `Mock 1 always free (${SESSION_QUESTION_TARGET} questions · score only), no account needed`;
}

export function freeTrialPublicLine() {
  return freeMockPublicLine();
}

/** Sitewide one-liner for footer / marketing — lifetime + monthly list prices. */
export function planPricingPublicLine() {
  const renewLine =
    'Lifetime is a one-time payment; monthly plans auto-renew and can be canceled anytime.';
  if (
    L2_ANCHOR_PRICE_USD !== L3_ANCHOR_PRICE_USD ||
    L2_MONTHLY_USD !== L3_MONTHLY_USD
  ) {
    return (
      `Level 1: $${ANCHOR_PRICE_USD} lifetime or $${L1_MONTHLY_USD}/month. ` +
      `Level 2: $${L2_ANCHOR_PRICE_USD} lifetime or $${L2_MONTHLY_USD}/month. ` +
      `Level 3: $${L3_ANCHOR_PRICE_USD} lifetime or $${L3_MONTHLY_USD}/month. ` +
      renewLine
    );
  }
  return (
    `Level 1: $${ANCHOR_PRICE_USD} lifetime or $${L1_MONTHLY_USD}/month. ` +
    `Levels 2 and 3: $${L2_ANCHOR_PRICE_USD} lifetime or $${L2_MONTHLY_USD}/month each. ` +
    renewLine
  );
}

export function freeTrialSharedPoolLine() {
  return freeMockPublicLine();
}

export function freeTrialCtaLabel() {
  return 'Try free (score only)';
}

/** What free users see after finishing Mock 1. */
export function freeMockResultsLine() {
  return 'Instant score free after Mock 1; topic breakdown, explanations, and post-mock analysis unlock with lifetime access';
}

/** Short line for meta descriptions and blog intros. */
export function freeMockBlogMetaTail() {
  return `${freeMockPublicLine()}. Score free; topic breakdown and explanations with lifetime access`;
}

/** Terms / privacy summary of the complimentary tier. */
export function freeMockLegalSummary() {
  return 'Mock 1 always free (90-question timed mock, score only) for signed-in accounts; lifetime or monthly access unlocks explanations and Mocks 2–8';
}

/** What lifetime access unlocks beyond the free mock. */
export function annualUnlockLine() {
  return 'Mocks 2–8, unlimited topic drills, explanations, post-mock analysis, and future L1 mocks we add';
}

export function freeMockSeoTitle() {
  return 'Free CFA Level 1 Mock Exam | CFA Mock Exam';
}

export function freeMockSeoDescription() {
  return 'CFA mock exam: free Level 1 Mock 1 anytime — timed CBT, BA II Plus, score only. No credit card. Unlock topic breakdown, explanations, and Mocks 2–8 with lifetime or monthly access.';
}

export function foundingSubhead(now = new Date()) {
  return `${freeMockPublicLine()} to start. ${foundingOfferPublicLine(now)} ${PAID_PASS_BANK_LINE}.`;
}

export function refundPolicyShortLine(days = REFUND_GRACE_DAYS) {
  return `${days}-day refund if you have not started any paid mock or topic drill`;
}

export function checkoutTermsBody(days = REFUND_GRACE_DAYS) {
  return `${refundPolicyShortLine(days)}; after that, fees are non-refundable except where required by law.`;
}

export function checkoutTermsLine() {
  return 'I agree to the Terms of Use.';
}

export function trustBullets(now = new Date()) {
  const pricing = getDynamicPricing(now);
  const refundLine = refundPolicyShortLine();
  if (!pricing.active) {
    return [
      `$${ANCHOR_PRICE_USD} standard lifetime access`,
      `${freeMockPublicLine()}; no payment required`,
      refundLine,
      'Access is applied to your account immediately after checkout',
    ];
  }
  return [
    `$${pricing.priceUsd} lifetime founding · list $${ANCHOR_PRICE_USD}`,
    `$${pricing.savingsUsd} below the $${ANCHOR_PRICE_USD} list price`,
    `Lifetime is one-time; monthly is optional at $${L1_MONTHLY_USD}/mo and cancel-anytime`,
    refundLine,
    'Access opens automatically after checkout — receipt email is your account',
  ];
}

/** @deprecated Use foundingOfferPublicLine() for live copy */
export const FOUNDING_OFFER_PUBLIC_LINE = foundingOfferPublicLine();
export const FOUNDING_OFFER_UNTIL_LINE = foundingOfferUntilLine();
export const SAVINGS_USD = ANCHOR_PRICE_USD - PASS_PRICE_USD;
export const FOUNDING_HEADLINE = foundingHeadline();
export const FOUNDING_SUBHEAD = foundingSubhead();
export const CHECKOUT_CTA = checkoutCta();
export const TRUST_BULLETS = trustBullets();

export function foundingCountdownLabel(state = getFoundingOfferState()) {
  if (state.expired) {
    return `Founding pricing has ended — standard rate $${ANCHOR_PRICE_USD} lifetime`;
  }
  if (state.daysLeft === 0) {
    return `Founding rate $${state.priceUsd} lifetime ends today (${state.hoursLeft}h ${state.minutesLeft}m remaining)`;
  }
  if (state.daysLeft === 1) {
    return `Founding rate $${state.priceUsd} lifetime ends tomorrow — standard rate $${ANCHOR_PRICE_USD} thereafter`;
  }
  return `Founding rate $${state.priceUsd} lifetime through ${state.endLabel} — ${state.daysLeft} days remaining`;
}

export function formatExamUrgency(examDate, now = new Date()) {
  const founding = getFoundingOfferState(now);
  const price = founding.priceUsd;
  const listNote =
    founding.active && price < ANCHOR_PRICE_USD
      ? `$${price} lifetime now ($${ANCHOR_PRICE_USD} list)`
      : `$${ANCHOR_PRICE_USD} lifetime`;

  if (!examDate) {
    if (founding.active) {
      return {
        daysLeft: null,
        headline: foundingHeadline(now),
        body: `${foundingSubhead(now)} ${foundingCountdownLabel(founding)}.`,
        cta: checkoutCta(now),
      };
    }
    return null;
  }

  const raw = String(examDate).slice(0, 10);
  const target = new Date(`${raw}T12:00:00.000Z`);
  if (Number.isNaN(target.getTime())) return null;
  const days = Math.ceil(
    (target - new Date(now.toISOString().slice(0, 10) + 'T12:00:00.000Z')) / 86400000
  );
  if (days < 0) return null;

  const countdownLine = founding.active ? ` ${foundingCountdownLabel(founding)}.` : '';

  if (days === 0) {
    return {
      daysLeft: 0,
      headline: 'Exam day — final preparation',
      body: `Lifetime access includes eight weighted timed mocks, post-mock analysis, and topic drills. ${listNote}.${countdownLine}`,
      cta: checkoutCta(now),
    };
  }
  if (days <= 7) {
    return {
      daysLeft: days,
      headline: `${days} day${days === 1 ? '' : 's'} until examination day`,
      body: `Your examination date is approaching. Eight weighted timed mocks, analysis, and topic drills at ${listNote}.${countdownLine}`,
      cta: checkoutCta(now),
    };
  }
  if (days <= 21) {
    return {
      daysLeft: days,
      headline: `${days} days until examination day`,
      body: `Primary preparation period. ${listNote} provides eight weighted mocks, analysis, topic drills, and ${QUESTION_BANK_SIZE}+ practice items.${countdownLine}`,
      cta: checkoutCta(now),
    };
  }
  if (days <= 45) {
    return {
      daysLeft: days,
      headline: `${days} days until your scheduled sitting`,
      body: `Secure ${listNote} for your preparation window.${countdownLine}`,
      cta: checkoutCta(now),
    };
  }
  return {
    daysLeft: days,
    headline: foundingHeadline(now),
    body: `${foundingSubhead(now)}${countdownLine}`,
    cta: checkoutCta(now),
  };
}
